import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { Form, Formik, FieldArray, useFormikContext } from 'formik';
import { unreachable } from 'hellospa/ts-utils';
import { ConnectFormikToStepper, StepperState, AutoSave } from 'hellospa/components/prep-and-send/formik-utils';
import { useAreFilesReady } from 'hellospa/components/prep-and-send/custom-hooks';
import { trackHeapCustomEvent } from 'common/utils/heap';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import useTriggers from 'hellospa/components/prep-and-send/trigger';
import CCSection from './cc';
import MergeFieldsSection, { OVERFLOW } from './merge-fields';
import DocumentSection from './document';
import EmailSection from './email';
import Recipients from './recipient';
import { FaxRecipientSection } from './fax-review';
import styles from './index.scss';
import messages from './intl';
import integrationMessages from './integration-intl';
import { Dispatch, StoreShape } from 'hellospa/redux/types';
import { PageHeader, SectionHeader } from '../header';
import SignerInfoModal from './signer-info-modal';
import UpgradeSendEmailModal from './upgrade-send-email-modal';
import OneTimeChargeModal from './one-time-charge-modal';
import UpgradeSendMultiFaxModal from './upgrade-send-multi-fax-modal';
import DocumentQuotaAlert from '../quota-alerts/document-quota-alert';
import Button, { ButtonType } from 'hellospa/foundation/control/button';
import * as Yup from 'yup';
import PreviewModal from './preview-modal';
import {
  ArrowDirection, TooltipPoint,
} from 'hellospa/foundation/control/tooltip/tooltip-ui';

export const reviewTriggerComponents = {
  UpgradeSendEmailModal,
  OneTimeChargeModal,
  SignerInfoModal,
  UpgradeSendMultiFaxModal,
};

type DownloadFileSectionProps = {
  setStage: types.TriggerCallback<() => {} | void>,
}
const DownloadFileSection = ({ setStage }:
  DownloadFileSectionProps) => {

  const state = useSelector((state: StoreShape) => state);
  const formik = useFormikContext();

  const isDownloadFileSectionEnabled = useSelector(selectors.isDownloadFileSectionEnabled);
  const intl = useIntl();
  const dispatch = useDispatch<Dispatch>();

  if (!isDownloadFileSectionEnabled) {
    return null;
  }

  const handleSelfSign = async () => {
    const shouldShowDocumentLimitModal = DocumentQuotaAlert.needsTrigger(state, formik);
    if (shouldShowDocumentLimitModal) {
      await setStage.submit();
    } else {
      await dispatch(actions.selfSaveAndRedirect());
    }

  };

  return (
    <div className={styles.container}>
      <SectionHeader text={intl.formatMessage(messages.headerDownloadDocument)} />
      <button
        type="button"
        data-qa-ref="download.button"
        className={styles.download}
        onClick={handleSelfSign}
      >
        {intl.formatMessage(messages.downloadDocumentButton)}
      </button>
      <div className={styles.orDivider}>
        <hr />
        <div className={styles.orIcon}>OR</div>
      </div>
    </div>
  );
};

const ReviewHeader = () => {
  const intl = useIntl();
  const workflow = useSelector(selectors.getWorkflow);
  const integration = useSelector(selectors.getIntegration);
  const isFaxRequest = workflow.tags.includes(types.WorkflowTags.SignatureRequestFax);
  const isMeOnly = useSelector(selectors.isMeOnly);

  const header = () => {
    if (integration) {
      switch (integration.name) {
        case types.IntegrationNames.OfficeSelfSign:
          // This integration doesn't need a custom header
          break;
        case types.IntegrationNames.DeepIntegration:
          return integrationMessages.deepintegration.headerDefaultReview;
        default:
          unreachable(integration.name);
      }
    }

    if (isFaxRequest) {
      return messages.headerFaxReview;
    }
    if (isMeOnly) {
      return messages.headerMeOnlyFlowReview;
    }
    return messages.headerDefaultReview;
  };

  return (
    <PageHeader text={intl.formatMessage(header())} />
  );
};

const FilesAreReadyValidation = () => {
  const files = useSelector(selectors.getFiles);
  const areFilesReady = useAreFilesReady(files);
  const intl = useIntl();
  const workflow = useSelector(selectors.getWorkflow);
  const transformState = React.useCallback((state: StepperState) => {
    if (workflow.name === types.FAX_NO_EDITOR && state.isValid) {
      return {
        isValid: areFilesReady,
        errorMessage: !areFilesReady
          ? intl.formatMessage(messages.filesAreStillLoading)
          : state.errorMessage,
      };
    }
    return state;

  }, [areFilesReady, intl, workflow.name]);

  return <ConnectFormikToStepper transformState={transformState} />;
};

const ReviewPage: types.WorkflowComponent = ({
  onSaveActions,
  formRef,
  triggers,
}) => {
  const dispatch = useDispatch<Dispatch>();

  const workflow = useSelector(selectors.getWorkflow);
  const isDocumentSectionEnabled = useSelector(selectors.isDocumentSectionEnabled);
  const isEmailSectionEnabled = useSelector(selectors.isEmailSectionEnabled);
  const recipientType = useSelector(selectors.getCCRecipientType);
  const allCCs = useSelector(selectors.getCCs);
  const intl = useIntl();
  const isInTemplatePreviewSplit = useSelector(selectors.allowTemplatePreview);

  // Right now we don't have a caching library setup for selectors, so if
  // filtering is done there it will produce a new array every time. This will
  // caclulate it once and keep that array
  let ccRecipients = React.useMemo(
    () => allCCs.filter(
      (cc) => cc.type === recipientType,
    ),
    [allCCs, recipientType],
  );
  const sendHighResFax = useSelector(selectors.isSendHighResFaxEnabled);
  const addCoverPage = useSelector(selectors.isAddCoverPageEnabled);
  const coverPage = useSelector(selectors.getCoverPage);
  let recipients = useSelector(selectors.getRecipientsForRequest);
  const recipientSchema = useSelector(selectors.getRecipientValidationSchema);
  const mergeFields = useSelector(selectors.getAllMergeFields);
  const documentInfo = useSelector(selectors.getDocument);
  const ccSchema = useSelector(selectors.getCCValidationSchema);
  const isMeOnly = useSelector(selectors.isMeOnly);
  const isTemplateLink = useSelector(selectors.isTemplateLinkRequest);
  const isTemplateRequest = useSelector(selectors.isTemplateRequest);
  const isBulkSend = useSelector(selectors.isBulkSend);
  const signerFile = useSelector(selectors.getBulkSendSignerFile);
  const userName = useSelector(selectors.getInitialValueOfNameFields);
  const isSignerInfoRequired = useSelector(selectors.isSignerInfoRequired);
  const isFaxRequest = workflow.tags.includes(types.WorkflowTags.SignatureRequestFax);
  const isEmbedded = useSelector(selectors.isEmbedded);
  const showTemplatePreviewButton = isInTemplatePreviewSplit && workflow
    .tags.includes(types.WorkflowTags.SignatureRequestTemplate)
    && !workflow.tags.includes(types.WorkflowTags.SignatureRequestDocument) && !isEmbedded;
  const editorFieldsIsIncomplete = useSelector(selectors.getEditorFieldsIsIncomplete);
  const templatePreviewHasFailed = showTemplatePreviewButton && editorFieldsIsIncomplete;

  const [isShowTemplatePreviewModal, setShowTemplatePreviewModal] = React.useState(false);


  const document = userName && isSignerInfoRequired
    ? { ...userName, ...documentInfo } : documentInfo;

  const defaultCCRecipients = React.useMemo(() => {
    return [actions.generateCCEmail()];
  }, []);

  const defaultRecipients = React.useMemo(() => {
    return [actions.generateFaxRecipient()];
  }, []);

  ccRecipients = isMeOnly && ccRecipients.length === 0
    ? defaultCCRecipients
    : ccRecipients;

  recipients = isFaxRequest && recipients.length === 0
    ? defaultRecipients
    : recipients;
  // Not sure how to make typescript happy with this schema
  const schema: Yup.ObjectSchema<InitialValues> | null = React.useMemo(
    (): Yup.ObjectSchema<any> | null => {
      let schema = null;
      if (recipientSchema) {
        if (isFaxRequest) {
          return recipientSchema.shape({ document: types.faxDocumentSchema });
        } else if (isTemplateLink || isBulkSend) {
          schema = recipientSchema;
        }
      }

      if (ccSchema && schema == null) {
        schema = ccSchema;
      }

      if (schema) {
        schema = schema.clone().shape({
          document: userName && isSignerInfoRequired
            ? types.userNameAndTitleSchema : types.documentSchema,
        });
      } else {
        schema = Yup.object();
      }

      return schema.clone().shape({
        mergeFields: Yup.array(Yup.mixed().test(
          'overflow',
          ({ label }) => intl.formatMessage(messages.mergeFieldOverflow, { label }),
          function checkOverflow(mergeField: types.MergeField) {
            // Validation can't run here because there is no access to DOM to
            // measure the text. <TextInput maintains this fake `.error`
            // property based on useSignerTextInput
            if (mergeField.error === OVERFLOW) {
              // This needs to match the path used in TextInput (./merge-fields)
              // so that it gets marked as touched as the user leaves the field.
              return this.createError({ path: `[${this.path}.field]` });
            }

            return true;
          },
        )),
      });
    },
    // eslint-disable-next-line max-len
    [ccSchema, intl, isBulkSend, isFaxRequest, isSignerInfoRequired, isTemplateLink, recipientSchema, userName],
  );

  const initialValues = {
    ccRecipients,
    recipients,
    mergeFields,
    document,
    sendHighResFax,
    addCoverPage,
    coverPage,
    signerFile, // required for bulk send
  };
  type InitialValues = typeof initialValues
  const triggerOptions = {
    load: () => { },
    submit: () => dispatch(actions.workflowOnSave(onSaveActions)),
    done: () => { },
  };

  // mimicking componentDidMount
  React.useEffect(() => {
    if (isTemplateRequest) {
      trackHeapCustomEvent('template_review_save_view');
    } else {
      trackHeapCustomEvent('sign_or_send_review_send');
    }
  }, [isTemplateRequest]);

  const [Triggers, setStage] = useTriggers(triggers, triggerOptions);

  const save = React.useCallback(async (values: InitialValues) => {
    if (isFaxRequest) {
      dispatch(actions.resetRecipients(values.recipients));
      dispatch(actions.setSettings({
        sendHighResFax: values.sendHighResFax,
        addCoverPage: values.addCoverPage,
      }));
      // Needs charge will be handled by /send
      dispatch(actions.setFlags({ needsCharge: false }));

      dispatch(actions.setCoverPage(values.coverPage));
    } else {
      dispatch(actions.setCCs(values.ccRecipients));
      values.mergeFields.map(
        (field) => dispatch(actions.updateField(field)),
      );
    }

    dispatch(actions.setDocument(values.document));
  }, [dispatch, isFaxRequest]);

  const submit = React.useCallback(async (values: InitialValues) => {
    // This is a hack because I don't know why the template preview modal is causing the form
    // to submit
    if (isShowTemplatePreviewModal) {
      return;
    }
    const normalizedValues: InitialValues = schema ? schema.cast(values) : values;
    await save(normalizedValues);

    if (isTemplateRequest) {
      trackHeapCustomEvent('template_save');
    } else {
      trackHeapCustomEvent('sign_or_send_for_signature');
    }

    // This won't resolve until the submit callback completes. This keeps the
    // promise chain intact from the onClick in the stepper button all the way
    // through making the request to the server in workflowOnSave.
    await setStage.submit();

  }, [isShowTemplatePreviewModal, isTemplateRequest, save, schema, setStage]);

  return (
    <div>
      <ReviewHeader />
      <PreviewModal
        isOpen={isShowTemplatePreviewModal}
        close={() => setShowTemplatePreviewModal(false)} />
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => submit(values)}
        validateOnChange={false}
        validationSchema={schema}
        validateOnMount={true}
        enableReinitialize={true}
        innerRef={types.useFormikRef(formRef)}
        // eslint-disable-next-line react/no-children-prop
        children={({ values, errors }) => (
          <Form>
            <Triggers />
            <FilesAreReadyValidation />
            <AutoSave
              values={values}
              errors={errors}
              saveToRedux={save}
              reduxSaveAction={actions.save} />

            <DownloadFileSection
              setStage={setStage} />

            <Recipients />

            <FieldArray name="ccRecipients" render={(arrayHelpers) => (
              <CCSection formikHelpers={arrayHelpers} />
            )} />

            <FieldArray name="recipients" render={(arrayHelpers) => (
              <FaxRecipientSection formikHelpers={arrayHelpers} />
            )} />

            <MergeFieldsSection />

            {showTemplatePreviewButton && (
              <div className={styles.templatePreviewButton}>
                <Button
                  kind={ButtonType.Outline}
                  disabled={templatePreviewHasFailed}
                  data-qa-ref='template-preview-button'
                  onClick={() => {
                    setShowTemplatePreviewModal(true);
                    trackHeapCustomEvent('Preview Button Click', {
                      Screen: 'Review',
                    });
                  }}
                >
                  {templatePreviewHasFailed && (
                    <TooltipPoint className={styles.tooltip} arrow={ArrowDirection.Bottom}>
                      {intl.formatMessage(messages.templatePreviewErrorTooltip)}
                    </TooltipPoint>
                  )}
                  {intl.formatMessage(messages.templatePreviewButton)}
                </Button>
              </div>
            )
            }
            {isDocumentSectionEnabled && !isEmailSectionEnabled && (
              <DocumentSection />
            )}

            {isEmailSectionEnabled && (
              <EmailSection />
            )}
          </Form>
        )} />
    </div>
  );
};

ReviewPage.getText = (workflow, state) => {
  const msg = defineMessages({
    title: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Review and send',
    },
    titleTemplate: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Review and save',
    },
    dropboxTitle: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Complete and send',
    },
    next: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Next',
    },
    sendDocument: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Send Document',
    },
    sendForSignature: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Send for signature',
    },
    saveTemplate: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Save template',
    },
    send: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Send',
    },
    save: {
      id: '',
      description: 'Label for button that advances user to the next step in the current workflow',
      defaultMessage: 'Save',
    },
  });

  const index = workflow.pages.findIndex((page) => page.component === ReviewPage);
  const isLastPage = (index + 1) === workflow.pages.length;

  let result: types.PageText = {
    title: msg.title,
    next: msg.next,
  };

  if (workflow.tags.includes(types.WorkflowTags.CreateTemplate)
    || workflow.tags.includes(types.WorkflowTags.CreateTemplateLink)
  ) {
    result = {
      ...result,
      title: msg.titleTemplate,
    };
  }

  // Different text if last page
  if (isLastPage) {
    switch (workflow.requestType) {
      case types.RequestTypes.EmbeddedRequest:
      case types.RequestTypes.SendTemplate:
      case types.RequestTypes.RequestSig:
      case types.RequestTypes.BulkSend:
        result = {
          ...result,
          next: msg.sendForSignature,
          shortNext: msg.send,
        };
        break;
      case types.RequestTypes.Demo:
      case types.RequestTypes.FinalizeDocGmail:
      case types.RequestTypes.SelfSave:
      case types.RequestTypes.SendDoc:
        result = {
          ...result,
          next: msg.sendDocument,
          shortNext: msg.send,
        };
        break;
      case types.RequestTypes.ReusableLink:
      case types.RequestTypes.Template:
      case types.RequestTypes.EmbeddedTemplate:
        result = {
          ...result,
          next: msg.saveTemplate,
          shortNext: msg.save,
        };
        break;
      case types.RequestTypes.ResponseSig:
      default:
    }
  }

  if (selectors.isMeOnly(state)) {
    result = {
      ...result,
      next: msg.sendDocument,
      shortNext: msg.send,
    };
  }

  // Dropbox
  if (workflow.tags.includes(types.WorkflowTags.DeepIntegration)) {
    result = {
      ...result,
      ...defineMessages({
        next: {
          id: '',
          description: 'text in the last button that sends a signature request',
          defaultMessage: 'Send for signature',
        },
      }),
      title: msg.dropboxTitle,
    };
  }

  return result;
};

export default ReviewPage;
